import { z } from 'zod';
import { UserRoleSchema } from './auth';
import { Dateish } from './common';
import { ResolvedTenantProfileSchema } from './profiles';
import type { TicketEventType } from './tickets';
import { UiLayoutSchema, UiPrefsSchema } from './ui-prefs';

/** Perfil próprio (área Minha conta) — shape do GET /me. */
export const ProfileSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  email: z.string(),
  role: z.enum(['admin', 'attendant', 'client']),
  phone: z.string().nullable(),
  extension: z.string().nullable(),
  /**
   * Keys efetivas do usuário (v7): união dos grupos de permissões a que
   * pertence; admin (e staff sem grupo nenhum — back-compat) = todas as
   * PERMISSION_KEYS. Opcional até o back preencher; ausente = sem restrição
   * (o front só esconde quando o campo VEM e a key falta).
   */
  permissions: z.array(z.string()).optional(),
  /**
   * Perfil do TENANT ativo, resolvido (spec 017, FR-004): vocabulário completo
   * (`terms`), recurso, naturezas e marca. Mesma política de `permissions`:
   * falha ao resolver OMITE o campo em vez de derrubar o /me — o front cai no
   * vocabulário neutro do produto.
   */
  tenantProfile: ResolvedTenantProfileSchema.optional(),
  /** preferências de interface desta pessoa neste tenant (FR-006) */
  uiPrefs: UiPrefsSchema.optional(),
  /** layout padrão do tenant — vale quando `uiPrefs.layout` está ausente */
  defaultLayout: UiLayoutSchema.optional(),
});
export type Profile = z.infer<typeof ProfileSchema>;

/**
 * GET /users/last-seen (admin) — último acesso de cada membro do tenant
 * (spec 017, FR-007), equipe e portal. `null` = nunca entrou desde que o
 * registro existe (2026-09-28: 128 logins de portal provisionados, uso
 * desconhecido). A escrita acontece no máximo a cada 5 min por pessoa.
 */
export const MemberAccessSchema = z.object({
  userId: z.number().int(),
  /** papel NESTE tenant (tenant_members.role) */
  role: UserRoleSchema,
  lastSeenAt: Dateish.nullable(),
});
export type MemberAccess = z.infer<typeof MemberAccessSchema>;

export const ProfilePatchBodySchema = z
  .object({
    name: z.string().min(1),
    email: z.string().email(),
    phone: z.string().nullable(),
    extension: z.string().nullable(),
  })
  .partial()
  .strict();
export type ProfilePatchBody = z.infer<typeof ProfilePatchBodySchema>;

export const PasswordChangeBodySchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(6),
});
export type PasswordChangeBody = z.infer<typeof PasswordChangeBodySchema>;

export const UserSessionSchema = z.object({
  // number no modo local (id da tabela de sessões); string UUID no modo
  // keycloak (id da sessão no Keycloak, mapeada pelo backend em GET /me/sessions).
  id: z.union([z.number().int(), z.string()]),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: Dateish,
  current: z.boolean(),
});
export type UserSession = z.infer<typeof UserSessionSchema>;

/** Tipos de notificação (feed do sino). Ausente nas prefs = habilitado. */
export const NOTIFICATION_KEYS = [
  'ticket_created',
  'comment_added',
  'stage_changed',
  'status_changed',
  'responsible_changed',
  'appointment_added',
] as const;
export type NotificationKey = (typeof NOTIFICATION_KEYS)[number];

/**
 * Tipo de evento da timeline → chave de preferência. Evento FORA do mapa não
 * notifica ninguém (nem sino, nem som, nem e-mail).
 *
 * Fonte ÚNICA (invariante 3): a API usa este mapa para decidir o que merece um
 * `ticket.event.created` no bus realtime e o web usa o MESMO mapa para filtrar
 * o feed do sino e o som — os dois lados não podem divergir sobre "o que é
 * notificável".
 */
export const TICKET_EVENT_TO_NOTIFICATION_KEY: Partial<Record<TicketEventType, NotificationKey>> = {
  created: 'ticket_created',
  comment_added: 'comment_added',
  stage_changed: 'stage_changed',
  status_changed: 'status_changed',
  responsible_changed: 'responsible_changed',
  appointment_added: 'appointment_added',
};

/** true quando o tipo de evento da timeline vira notificação para alguém. */
export function isNotifiableTicketEvent(type: TicketEventType): boolean {
  return TICKET_EVENT_TO_NOTIFICATION_KEY[type] !== undefined;
}

/**
 * Categoria notificável que NÃO nasce da timeline de ticket (v12): mensagem
 * nova do cliente no chat. Fica FORA de NOTIFICATION_KEYS de propósito —
 * aquela lista é o vocabulário dos eventos de ticket (o funil de e-mail
 * notify-responsible itera sobre ela, e chat não tem responsável de ticket nem
 * template de e-mail). O que ela compartilha é só o armazenamento: a mesma
 * jsonb `users.notification_prefs` (catchall) e a mesma regra "ausente =
 * habilitado" (default ON).
 */
export const CHAT_MESSAGE_NOTIFICATION_KEY = 'chat_message' as const;

/**
 * TODAS as chaves de categoria aceitas nas prefs (sino + som + navegador):
 * as de ticket mais a de chat. É esta lista — não NOTIFICATION_KEYS — que a
 * API valida no PUT e que a Minha conta renderiza.
 */
export const NOTIFICATION_PREF_KEYS = [
  ...NOTIFICATION_KEYS,
  CHAT_MESSAGE_NOTIFICATION_KEY,
] as const;
export type NotificationPrefKey = (typeof NOTIFICATION_PREF_KEYS)[number];

/**
 * Prefs de notificação no jsonb `users.notification_prefs`. As chaves de
 * NOTIFICATION_PREF_KEYS seguem no catchall (ausente = habilitado, default-on);
 * `emailEnabled` (v5) é a exceção tipada: "receber também por E-MAIL os
 * eventos habilitados" — default FALSE, e linha antiga sem o campo lê como
 * false (por isso opcional + checagem `=== true` nos consumidores, nunca
 * "ausente = ligado").
 *
 * v12 (realtime in-app): `soundEnabled` é default-ON (ausente = com som — só
 * `=== false` cala) e `browserEnabled` é default-OFF como o e-mail, porque
 * depende de permissão explícita do navegador.
 */
export const NotificationPrefsSchema = z
  .object({
    emailEnabled: z.boolean().optional(),
    soundEnabled: z.boolean().optional(),
    browserEnabled: z.boolean().optional(),
    /**
     * spec 037 (PR B): a pessoa escolheu o e-mail por evento na coluna E-mails
     * — dali em diante valem as células `email_*` (notification-prefs.ts). Sem
     * ele, o e-mail segue a regra antiga do `emailEnabled`.
     */
    emailPerEvent: z.boolean().optional(),
  })
  .catchall(z.boolean());
export type NotificationPrefs = z.infer<typeof NotificationPrefsSchema>;

export const TwoFactorStatusSchema = z.object({
  /** false no modo local (2FA é do Keycloak) */
  supported: z.boolean(),
  totpConfigured: z.boolean(),
});
export type TwoFactorStatus = z.infer<typeof TwoFactorStatusSchema>;
